import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { listProjects } from '@/services/projectService';
import {
  getStepAnalysis,
  type StepAnalysisRun,
  type StepAnalysisStep,
} from '@/services/adminService';
import type { ScriptProject } from '@/types/scriptProject';
import { methodologyPhaseLabel } from '@/config/methodologyAnalysisPipeline';
import { formatScriptId } from '@/utils/formatIndexId';

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function stepChipClasses(level: StepAnalysisStep['level']) {
  return cn(
    'inline-flex max-w-[220px] flex-col gap-0.5 rounded border px-2 py-1 text-[10px] font-mono leading-tight',
    level === 'ok' && 'border-green-500/50 bg-green-950/50 text-green-300',
    level === 'fallthrough' && 'border-amber-500/50 bg-amber-950/40 text-amber-200',
    level === 'failed' && 'border-red-500/50 bg-red-950/50 text-red-300',
  );
}

function StepChip({ step }: { step: StepAnalysisStep }) {
  const title =
    methodologyPhaseLabel(
      step.strand_id ? `${step.strand_id}:${step.step_id}` : step.step_id,
    ) || step.step_id;
  return (
    <div className={stepChipClasses(step.level)} title={step.detail}>
      <span className="font-semibold uppercase tracking-wide">{step.label}</span>
      <span className="truncate text-white/70">{title}</span>
      {step.level !== 'ok' && step.detail && step.detail !== 'ok' && (
        <span className="line-clamp-2 text-[9px] text-white/55">{step.detail}</span>
      )}
    </div>
  );
}

export default function StepAnalysisPanel() {
  const [projects, setProjects] = useState<ScriptProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [runs, setRuns] = useState<StepAnalysisRun[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError(null);
    try {
      const list = await listProjects();
      setProjects(list);
      setSelectedProjectId((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        return list[0]?.id ?? null;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load mandates');
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadRuns = useCallback(async (projectId: string | null) => {
    setLoadingRuns(true);
    setError(null);
    try {
      const data = await getStepAnalysis({
        projectId: projectId ?? undefined,
        limit: 40,
      });
      setRuns(data.runs);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load step analysis');
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    void loadRuns(selectedProjectId);
  }, [selectedProjectId, loadRuns]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-mono text-white/45 max-w-2xl">
          Methodology step outcomes per run — green ok, amber fall-through, red failed.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-mono text-white/40">
            Mandate
            <select
              className="ml-2 rounded border border-[#2a2a2a] bg-[#141414] px-2 py-1.5 text-sm text-white"
              value={selectedProjectId ?? ''}
              disabled={loadingProjects}
              onChange={(e) => setSelectedProjectId(e.target.value || null)}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <Button
            variant="indexOutline"
            size="sm"
            onClick={() => void loadRuns(selectedProjectId)}
            disabled={loadingRuns}
          >
            Refresh
          </Button>
        </div>
      </div>

      {selectedProject && (
        <p className="mb-4 text-xs font-mono text-white/35">
          Showing runs for {selectedProject.name}
        </p>
      )}

      {error && (
        <div className="mb-4 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {loadingRuns ? (
        <div className="flex items-center gap-2 py-12 text-white/50 font-sans text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading step analysis…
        </div>
      ) : runs.length === 0 ? (
        <p className="py-12 text-center text-sm text-white/45 font-sans">
          No methodology step results found for this mandate yet.
        </p>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => {
            const open = expanded[run.script_id] ?? true;
            return (
              <section
                key={run.script_id}
                className="rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] overflow-hidden"
              >
                <button
                  type="button"
                  className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
                  onClick={() =>
                    setExpanded((prev) => ({ ...prev, [run.script_id]: !open }))
                  }
                >
                  {open ? (
                    <ChevronDown className="mt-1 h-4 w-4 text-white/40 shrink-0" />
                  ) : (
                    <ChevronRight className="mt-1 h-4 w-4 text-white/40 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h3 className="text-sm font-sans font-semibold text-white truncate">
                        {run.script_name}
                      </h3>
                      <span className="text-[11px] font-mono text-white/40">
                        {formatScriptId(run.script_id)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs font-mono text-white/45">
                      {formatDate(run.date_run)} · {run.step_count} steps ·{' '}
                      <span className="text-green-400">{run.ok_count} ok</span>
                      {' · '}
                      <span className="text-amber-300">{run.fallthrough_count} fall-through</span>
                      {' · '}
                      <span className="text-red-300">{run.failed_count} failed</span>
                    </p>
                  </div>
                </button>
                {open && (
                  <div className="border-t border-[#2a2a2a] px-4 py-3 flex flex-wrap gap-2">
                    {run.steps.map((step) => (
                      <StepChip
                        key={`${step.strand_id || ''}:${step.step_id}:${step.created_at}`}
                        step={step}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
